/**
 * Image Text Tabs Component
 * Handles sticky navigation with scroll-based active state changes
 */

class ImageTextTabs extends HTMLElement {
  constructor() {
    super();
    this.navigation = this.querySelector('[data-sticky-nav]');
    this.content = this.querySelector('[data-steps-content]');
    this.navItems = this.querySelectorAll('.step-nav-item');
    this.contentItems = this.querySelectorAll('.step-content-item');
    this.isScrolling = false;
    this.lastActiveIndex = 0;
    
    if (this.navigation && this.content && this.contentItems.length > 0) {
      this.init();
    }
  }

  init() {
    // Set up intersection observer for content items
    this.setupIntersectionObserver();
    
    // Add click handlers to navigation items
    this.setupNavigationClicks();
    
    // Handle resize events
    this.handleResize = this.debounce(() => {
      this.setupIntersectionObserver();
    }, 250);
    
    window.addEventListener('resize', this.handleResize);
  }

  setupIntersectionObserver() {
    // Disconnect existing observer if any
    if (this.observer) {
      this.observer.disconnect();
    }

    // Create intersection observer options
    const options = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Trigger when item is in the middle 20% of viewport
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    // Create the observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
          const index = parseInt(entry.target.getAttribute('data-step-item'));
          if (!isNaN(index) && index !== this.lastActiveIndex) {
            this.setActiveStep(index);
            this.lastActiveIndex = index;
          }
        }
      });
    }, options);

    // Observe all content items
    this.contentItems.forEach((item) => {
      this.observer.observe(item);
    });
  }

  setupNavigationClicks() {
    this.navItems.forEach((navItem) => {
      const link = navItem.querySelector('.step-nav-link');
      if (link) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          
          const index = parseInt(navItem.getAttribute('data-step-index'));
          if (!isNaN(index) && this.contentItems[index]) {
            // Set active immediately
            this.setActiveStep(index);
            
            // Scroll to the content item using getBoundingClientRect for accurate position
            const targetItem = this.contentItems[index];
            const offset = 100; // Offset from top\
            const targetPosition = targetItem.getBoundingClientRect().top + window.scrollY - offset;
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        });
      }
    });
  }

  setActiveStep(index) {
    // Remove active class from all nav items
    this.navItems.forEach((item) => {
      item.classList.remove('active');
    });

    // Add active class to the current nav item
    const currentNavItem = this.querySelector(`[data-step-index="${index}"]`);
    if (currentNavItem) {
      currentNavItem.classList.add('active');
      
      // On mobile, scroll the navigation into view
      if (window.innerWidth < 992) {
        currentNavItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  disconnectedCallback() {
    // Clean up observer and event listeners
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }
}

// Register the custom element
if (!customElements.get('image-text-tabs')) {
  customElements.define('image-text-tabs', ImageTextTabs);
}

